/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Tooltip,
  Spin,
  Switch,
  Button,
  Modal,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import AutoArchiveSettings from './AutoArchiveSettings';
import AutoArchiveOnLabelSettings from './AutoArchiveOnLabelSettings';
import EmrAutoUploadCategorySettings from './EmrAutoUploadCategorySettings';
import ModmedQueuedFaxSettings from './ModmedQueuedFaxSettings';
import KillSwitchSettings from './KillSwitchSettings';
import EmaResultsProviderSettings from './EmaResultsProviderSettings';
import {
  getAutoArchiveSettings,
  updateAutoArchiveSettings,
  getAutoArchiveOnLabelSettings,
  updateAutoArchiveOnLabelSettings,
  getAllLabelsForAutoArchive,
  getDocumentCategories,
  getEmrAutoUploadCategorySettings,
  updateEmrAutoUploadCategorySettings,
  getEmaResultsProviderSettings,
  updateEmaResultsProviderSettings,
  getModmedQueuedFaxSettings,
  updateModmedQueuedFaxSettings,
  getArmKillSwitchSettings,
  updateArmKillSwitchSettings,
  getEmrTemplates,
  getEmrFacilities,
} from '../../../actions/userProfile';
import { captureAnalytics } from '../../../actions/preload';
import '../../modal/settings-modal.scss';

const settingPaused = (value) => value === true || value === 'true';
const settingEnabled = (settings) => settings?.enabled === true || settings?.enabled === 'true';
const hasUploadDedupEnforcementFeature = (activeFeatures) => (
  activeFeatures.includes('upload_dedup_enforce')
);

const AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG = {
  name: 'Auto Archive Received Fax After Autoupload',
  id: 'auto_archive_fax_after_autoupload',
  description: 'Automatically archive received fax following the success of a manual auto upload attempt.',
  hasConfig: true,
};

const UPLOAD_DEDUP_ENFORCEMENT_FLAG = {
  name: 'Duplicate Upload Enforcement',
  id: 'upload_dedup_enforce',
  description: 'Skip duplicate pharmacy request uploads during automatic upload. Category selection is configured in Settings → Categories → Auto-upload.',
  hasConfig: true,
};

const EMA_RESULTS_ORDERING_PROVIDER_FLAG = {
  name: 'Results Ordering Provider',
  id: 'ema_results_ordering_provider',
  description: 'For documents in "Results - " categories, automatically match the AI-extracted ordering provider against your synced EMA provider list and assign them when uploading to EMA.',
  hasConfig: true,
};

const EMA_HIDE_NATIVE_SEND_FLAG = {
  name: 'Hide ModMed Native Send in EMA',
  id: 'ema_hide_native_send',
  description: 'Hides ModMed\'s built-in Send button on EMA\'s template fax page so staff send faxes through Medsender via the Chrome extension.',
};

const MODMED_QUEUED_FAX_FLAG = {
  name: 'ModMed Queued Fax',
  id: 'modmed_queued_fax',
  description: 'Automatically poll ModMed\'s pending fax queue, send faxes via MedSender, and optionally upload to patient charts.',
  hasConfig: true,
};

const ARM_AUTO_PROCESSING_FLAG = {
  name: 'ARM Auto-Processing',
  id: 'arm_enabled',
  description: 'Pause or resume automatic ARM publishing for this organization. This producer-side control stops new signals from being published, but queued or in-flight referrals may still run.',
  hasConfig: true,
};

const AUTO_ARCHIVE_ON_LABEL_FLAG = {
  name: 'Archive by Label or Category',
  id: 'auto_archive_on_label',
  description: 'Archives an inbound document automatically when it carries a label or category you select, whether that label was applied by a person or by automation. Archives nothing until you make a selection, and leaves any document someone is assigned to.',
  hasConfig: true,
};

const CONFIG_BACKED_FEATURES = [
  {
    id: AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG.id,
    settingsProp: 'autoArchiveSettings',
    flag: AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG,
    enabledFromSettings: settingEnabled,
  },
  {
    id: UPLOAD_DEDUP_ENFORCEMENT_FLAG.id,
    settingsProp: 'emrAutoUploadCategorySettings',
    flag: UPLOAD_DEDUP_ENFORCEMENT_FLAG,
    enabledFromSettings: (settings) => (
      settings?.upload_dedup_enabled === true || settings?.upload_dedup_enabled === 'true'
    ),
    shouldAdd: (settings) => Boolean(settings?.emr),
  },
  {
    id: EMA_RESULTS_ORDERING_PROVIDER_FLAG.id,
    settingsProp: 'emaResultsProviderSettings',
    flag: EMA_RESULTS_ORDERING_PROVIDER_FLAG,
    getSettings: (props) => props.emaResultsProviderSettings?.settings,
    enabledFromSettings: settingEnabled,
  },
  {
    id: MODMED_QUEUED_FAX_FLAG.id,
    settingsProp: 'modmedQueuedFaxSettings',
    flag: MODMED_QUEUED_FAX_FLAG,
    enabledFromSettings: settingEnabled,
    shouldAdd: (settings) => Boolean(settings?.has_ema_credentials),
  },
  {
    id: AUTO_ARCHIVE_ON_LABEL_FLAG.id,
    settingsProp: 'autoArchiveOnLabelSettings',
    flag: AUTO_ARCHIVE_ON_LABEL_FLAG,
    enabledFromSettings: settingEnabled,
    // Only render the row once the org actually has the flag. Without this the
    // row would show for every org and its settings fetch would 404 wherever the
    // endpoint has not shipped yet.
    shouldAdd: (_settings, props) => props.allActiveFeatures.includes('auto_archive_on_label'),
  },
  {
    id: ARM_AUTO_PROCESSING_FLAG.id,
    settingsProp: 'armKillSwitchSettings',
    flag: ARM_AUTO_PROCESSING_FLAG,
    enabledFromSettings: (settings) => !settingPaused(settings?.auto_processing_paused),
    shouldAdd: (_settings, props) => props.allActiveFeatures.includes('arm_enabled'),
  },
];

const isConfigBackedFeature = (id) => CONFIG_BACKED_FEATURES.some((config) => config.id === id);

const HIDE_COVERSHEET_FLAG = {
  name: 'Hide Coversheet',
  id: 'hide_coversheet',
  description: 'Displays a checkbox in the fax send dialog and removes the cover sheet only for a fax where the user checks the box.',
};

export class ToggleFeatures extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAutoArchiveSettingsModal: false,
      autoEnableAutoArchive: false,
      showModmedSettingsModal: false,
      autoEnableModmed: false,
      showArmKillSwitchModal: false,
      showAutoArchiveOnLabelModal: false,
      autoArchiveOnLabelRequestedEnabled: null,
      showUploadDedupSettingsModal: false,
      autoEnableUploadDedup: null,
      showEmaResultsProviderSettingsModal: false,
      emaResultsProviderRequestedEnabled: null,
      featureFlags: [
        {
          name: 'Publish Forms', id: 'publish_forms', description: 'Allows users to publish forms in the uploads view. This creates a unique link for the record that users can embed or share for patients to fill out forms. Form submissions are viewable in the uploads view.', enabled: false,
        },
        {
          name: 'Personalized Address Book', id: 'personalized_address_book', description: 'Allows the address book to be assigned to the user instead of the whole org.', enabled: false,
        },
        {
          name: 'Admin Lock Labels', id: 'manage_label', description: 'When enabled, only admins can create/edit/delete labels.', enabled: false,
        },
        {
          name: 'Admin Lock Address Book', id: 'admin_lock_address_book', description: 'If enabled, only Admins can create, edit, or delete contacts from the address book.', enabled: false,
        },
        {
          name: 'Admin Lock Categories', id: 'manage_category', description: 'If enabled, only Admins can create, edit, or delete document categories.', enabled: false,
        },
        {
          name: 'Department Labels', id: 'department_labels', description: 'Allows labels to be on a per department basis rather for the whole org', enabled: false,
        },
        {
          name: 'Flatten Annotations', id: 'flatten_annotations', description: "When the user saves changes to a document, those changes become permanent and can't be removed. Useful for signatures if the user doesn't want the signature to be able to be removed by others in the future.", enabled: false,
        },
        {
          name: 'Add Name And Date To Signature', id: 'add_name_and_date_to_signature', description: "When signing a document in record viewer we will add the name of the user and date to the signature. For example, it'd say \"Signed By Test User on 4/3/2024.\"", enabled: false,
        },
        {
          name: 'Default To Dept Name', id: 'default_to_dept_name', description: "If a fax number is owned by a department, when sending a fax we'll use the departments name rather than the organizations name to list on the coversheet we generate.", enabled: false,
        },
        {
          name: 'Remove Patient Info In Coversheet', id: 'remove_patient_info_in_coversheet', description: "We will no longer include the patient's name and DOB in the coversheet we generate when the user sends a fax.", enabled: false,
        },
        {
          name: 'Manual AI on Uploads', id: 'manual_ai_on_uploads', description: 'Lets permitted users trigger AI extraction on uploaded documents on demand. Admins always have access; other staff can be given access through roles where role-based access is enabled. Pages from successful runs are billed to your subscription as inbound transmission usage.', enabled: false,
        },
      ],
    };
    this.openConfigModal = this.openConfigModal.bind(this);
  }

  // requestedEnabled carries the table switch's requested value (true/false)
  // into the modal; null (Configure button) defers to the saved settings.
  openConfigModal(featureId, requestedEnabled = null) {
    if (featureId === 'auto_archive_fax_after_autoupload') {
      captureAnalytics('auto_archive_fax_after_autoupload_settings_opened', {
        action: requestedEnabled === null ? 'configure' : 'toggle',
        enabled: requestedEnabled,
      });
      this.setState({
        showAutoArchiveSettingsModal: true,
        autoEnableAutoArchive: requestedEnabled === true,
      });
      this.props.getAutoArchiveSettings();
      return;
    }

    if (featureId === 'auto_archive_on_label') {
      this.setState({
        showAutoArchiveOnLabelModal: true,
        autoArchiveOnLabelRequestedEnabled: requestedEnabled,
      });
      this.props.getAutoArchiveOnLabelSettings();
      this.props.getAllLabelsForAutoArchive();
      this.props.getDocumentCategories();
      return;
    }

    if (featureId === 'arm_enabled') {
      this.setState({ showArmKillSwitchModal: true });
      this.props.getArmKillSwitchSettings();
      return;
    }

    if (featureId === 'ema_results_ordering_provider') {
      this.setState({
        showEmaResultsProviderSettingsModal: true,
        emaResultsProviderRequestedEnabled: requestedEnabled,
      });
      this.props.getEmaResultsProviderSettings();
      return;
    }

    if (featureId === 'modmed_queued_fax') {
      this.setState({ showModmedSettingsModal: true, autoEnableModmed: requestedEnabled === true });
      // Fetch templates and facilities only when modal opens, and only if not already loaded
      if (!this.props.emrTemplatesLoaded && !this.props.emrTemplatesError) {
        this.props.getEmrTemplates();
      }
      if (!this.props.emrFacilitiesLoaded && !this.props.emrFacilitiesError) {
        this.props.getEmrFacilities();
      }
      return;
    }

    if (featureId === 'upload_dedup_enforce') {
      this.setState({
        showUploadDedupSettingsModal: true,
        autoEnableUploadDedup: requestedEnabled,
      });
      this.props.getEmrAutoUploadCategorySettings();
    }
  }

  componentDidMount = async () => {
    const activeFeatures = this.props.allActiveFeatures;

    if (activeFeatures.includes('manual_upload_emr')) {
      this.props.getAutoArchiveSettings();
    }

    // Fetch the organization-wide EMA signal, including multi-EMR organizations.
    const isEmaOrg = localStorage.m8_auto_upload_emr === 'EMA';
    this.props.getModmedQueuedFaxSettings();

    // Fetch ARM kill-switch settings up front (alongside the modmed settings
    // fetch) so componentDidUpdate can reconcile the row's enabled state once
    // they load. The featureFlags-build block below reads the current props for
    // the initial enabled value.
    if (activeFeatures.includes('arm_enabled')) {
      this.props.getArmKillSwitchSettings();
    }

    // Same reasoning for Archive by Label or Category: subscriptions are often
    // already in Redux by the time this tab mounts (an admin revisiting Settings,
    // or the lazily rendered Features tab), in which case componentDidUpdate's
    // false-to-true check never fires.
    if (activeFeatures.includes('auto_archive_on_label')) {
      this.props.getAutoArchiveOnLabelSettings();
    }

    // The Features page owns duplicate-upload enforcement only. Category selection
    // lives in Settings -> Categories -> Auto-upload.
    if (hasUploadDedupEnforcementFeature(activeFeatures)) {
      this.props.getEmrAutoUploadCategorySettings();
    }

    const { featureFlags } = this.state;
    featureFlags.forEach((feature, index) => {
      if (activeFeatures.includes(feature.id)) {
        featureFlags[index].enabled = true;
      }
    });

    if (activeFeatures.includes('direct_faxes')) {
      featureFlags.push({
        ...HIDE_COVERSHEET_FLAG,
        enabled: activeFeatures.includes(HIDE_COVERSHEET_FLAG.id),
      });
    }
    if (activeFeatures.includes('referral_management')) {
      featureFlags.push({
        name: 'Only show referrals in Referral Management', id: 'hide_referrals', description: 'Will no longer show documents categorized as "referral" in the inbox and only show them in the "referral management" view.', enabled: activeFeatures.includes('hide_referrals'),
      });
    }

    if (activeFeatures.includes('manual_upload_emr')) {
      featureFlags.push(
        {
          ...AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG,
          enabled: settingEnabled(this.props.autoArchiveSettings) || activeFeatures.includes('auto_archive_fax_after_autoupload'),
        },
        {
          name: 'Hide Task Creation Button',
          id: 'hide_task_creation_button',
          description: 'Allows users to hide the Create Task Button',
          enabled: activeFeatures.includes('hide_task_creation_button'),
        },
      );
    }

    if (localStorage.m8_auto_upload_emr === 'EMA') {
      featureFlags.push({
        name: 'Auto Upload After Sending Fax',
        id: 'auto_upload_after_sending_fax',
        description: 'Automatically upload a fax after it has been sent.',
        enabled: activeFeatures.includes('auto_upload_after_sending_fax'),
      });
      featureFlags.push({
        name: 'Auto Upload Referral Reply',
        id: 'auto_upload_referral_reply',
        description: 'Automatically upload a referral reply to the patient\'s chart after it has been sent.',
        enabled: activeFeatures.includes('auto_upload_referral_reply'),
      });
    }

    // Results Ordering Provider is granted per-org by Medsender (the feature
    // subscription is the availability gate); org admins configure behavior.
    // The subscription is also the visibility gate here — the locally cached
    // EMR type comes from an arbitrary credential, so it can hide the row for
    // orgs with multiple EMRs. EMA-only behavior is enforced where it matters:
    // the grant is EMA-only and the worker checks the EMA credential.
    if (activeFeatures.includes('ema_results_ordering_provider')) {
      this.props.getEmaResultsProviderSettings();
      const emaResultsSettings = this.props.emaResultsProviderSettings?.settings;
      featureFlags.push({
        ...EMA_RESULTS_ORDERING_PROVIDER_FLAG,
        enabled: settingEnabled(emaResultsSettings),
      });
    }

    // Show ModMed Queued Fax toggle if org has EMA credentials (even before feature is enabled)
    // This allows admins to enable the feature for the first time
    const modmedSettings = this.props.modmedQueuedFaxSettings;
    const hasEmaCredentials = modmedSettings?.has_ema_credentials === true || isEmaOrg;
    if (hasEmaCredentials || activeFeatures.includes(EMA_HIDE_NATIVE_SEND_FLAG.id)) {
      featureFlags.push({ ...EMA_HIDE_NATIVE_SEND_FLAG, enabled: activeFeatures.includes(EMA_HIDE_NATIVE_SEND_FLAG.id) });
    }
    if (hasEmaCredentials || activeFeatures.includes('modmed_queued_fax')) {
      featureFlags.push({
        ...MODMED_QUEUED_FAX_FLAG,
        enabled: settingEnabled(modmedSettings),
      });
    }

    // ARM auto-processing pause toggle. Gated on the org actually having the
    // arm_enabled feature; the row exposes a per-org pause (not a feature
    // enable/disable) edited in a confirmation-guarded modal.
    if (activeFeatures.includes('arm_enabled')) {
      const armPaused = settingPaused(this.props.armKillSwitchSettings?.auto_processing_paused);
      featureFlags.push({
        ...ARM_AUTO_PROCESSING_FLAG,
        enabled: !armPaused,
      });
    }

    // Push the row from current props rather than waiting on a settings change:
    // syncConfigBackedFeatureRows only inserts it when the settings prop changes,
    // so an org whose subscriptions were already in Redux at mount would never
    // see the row. It still reconciles `enabled` once the settings land.
    if (activeFeatures.includes('auto_archive_on_label')) {
      featureFlags.push({
        ...AUTO_ARCHIVE_ON_LABEL_FLAG,
        enabled: settingEnabled(this.props.autoArchiveOnLabelSettings),
      });
    }

    this.setState({ featureFlags: [...featureFlags] });
  };

  reconcileConfigBackedFeatureRows(prevProps, featureFlags) {
    const changedConfigs = CONFIG_BACKED_FEATURES.filter((config) => (
      prevProps[config.settingsProp] !== this.props[config.settingsProp]
    ));
    if (changedConfigs.length === 0) return featureFlags;

    let nextFeatureFlags = featureFlags;
    let changed = false;

    changedConfigs.forEach((config) => {
      const settings = config.getSettings ? config.getSettings(this.props) : this.props[config.settingsProp];
      const enabled = config.enabledFromSettings(settings);
      const existingFlag = nextFeatureFlags.find((flag) => flag.id === config.id);

      if (existingFlag) {
        if (existingFlag.enabled !== enabled) {
          nextFeatureFlags = nextFeatureFlags.map((flag) => (
            flag.id === config.id ? { ...flag, enabled } : flag
          ));
          changed = true;
        }
      } else if (config.flag && config.shouldAdd?.(settings, this.props)) {
        nextFeatureFlags = [...nextFeatureFlags, {
          ...config.flag,
          enabled,
        }];
        changed = true;
      }
    });

    return changed ? nextFeatureFlags : featureFlags;
  }

  // Kept as a small compatibility wrapper for callers that reconcile one source at a time.
  // eslint-disable-next-line react/no-unused-class-component-methods
  syncConfigBackedFeatureRows(prevProps) {
    const { featureFlags } = this.state;
    const nextFeatureFlags = this.reconcileConfigBackedFeatureRows(prevProps, featureFlags);
    if (nextFeatureFlags !== featureFlags) this.setState({ featureFlags: nextFeatureFlags });
  }

  // UserProfile fetches active features after this tab first mounts. Keep
  // direct-fax-only rows in sync when that asynchronous result arrives rather
  // than requiring an admin to leave and revisit the Features tab.
  reconcileHideCoversheetFlag = (featureFlags) => {
    const { allActiveFeatures } = this.props;
    const nextFeatureFlags = featureFlags.filter((flag) => flag.id !== HIDE_COVERSHEET_FLAG.id);

    if (allActiveFeatures.includes('direct_faxes')) {
      nextFeatureFlags.push({
        ...HIDE_COVERSHEET_FLAG,
        enabled: allActiveFeatures.includes(HIDE_COVERSHEET_FLAG.id),
      });
    }

    return nextFeatureFlags;
  };

  reconcileManualUploadEmrFlags = (featureFlags) => {
    const { allActiveFeatures } = this.props;
    const hasManualUploadEmr = allActiveFeatures.includes('manual_upload_emr');
    const manualUploadFeatureIds = [
      AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG.id,
      'hide_task_creation_button',
    ];
    const nextFeatureFlags = featureFlags.filter((flag) => (
      hasManualUploadEmr || !manualUploadFeatureIds.includes(flag.id)
    ));

    if (!hasManualUploadEmr) return nextFeatureFlags;

    if (!nextFeatureFlags.some((flag) => flag.id === AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG.id)) {
      nextFeatureFlags.push({
        ...AUTO_ARCHIVE_FAX_AFTER_AUTOUPLOAD_FLAG,
        enabled: settingEnabled(this.props.autoArchiveSettings)
          || allActiveFeatures.includes('auto_archive_fax_after_autoupload'),
      });
    }
    if (!nextFeatureFlags.some((flag) => flag.id === 'hide_task_creation_button')) {
      nextFeatureFlags.push({
        name: 'Hide Task Creation Button',
        id: 'hide_task_creation_button',
        description: 'Allows users to hide the Create Task Button',
        enabled: allActiveFeatures.includes('hide_task_creation_button'),
      });
    }

    return nextFeatureFlags;
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  syncHideCoversheetFlag = () => {
    this.setState(({ featureFlags }) => ({
      featureFlags: this.reconcileHideCoversheetFlag(featureFlags),
    }));
  };

  reconcileFeatureFlags = (prevProps) => {
    const activeFeaturesChanged = prevProps.allActiveFeatures !== this.props.allActiveFeatures;
    const configBackedFeatureChanged = CONFIG_BACKED_FEATURES.some((config) => (
      prevProps[config.settingsProp] !== this.props[config.settingsProp]
    ));
    if (!activeFeaturesChanged && !configBackedFeatureChanged) return;

    this.setState(({ featureFlags }) => {
      let nextFeatureFlags = featureFlags;
      if (activeFeaturesChanged) {
        nextFeatureFlags = this.reconcileHideCoversheetFlag(nextFeatureFlags);
        nextFeatureFlags = this.reconcileManualUploadEmrFlags(nextFeatureFlags);
      }
      if (configBackedFeatureChanged) {
        nextFeatureFlags = this.reconcileConfigBackedFeatureRows(prevProps, nextFeatureFlags);
      }
      return nextFeatureFlags === featureFlags ? null : { featureFlags: nextFeatureFlags };
    });
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.allActiveFeatures.includes('manual_upload_emr')
      && this.props.allActiveFeatures.includes('manual_upload_emr')) {
      this.props.getAutoArchiveSettings();
    }

    // Active features load after mount (UserProfile fetches them in its own
    // componentDidMount, which runs after this child's). Re-check when they arrive
    // so a direct load of this tab still fetches duplicate-upload settings.
    if (prevProps.allActiveFeatures !== this.props.allActiveFeatures
      && !hasUploadDedupEnforcementFeature(prevProps.allActiveFeatures)
      && hasUploadDedupEnforcementFeature(this.props.allActiveFeatures)) {
      this.props.getEmrAutoUploadCategorySettings();
    }

    if (prevProps.allActiveFeatures !== this.props.allActiveFeatures
      && !prevProps.allActiveFeatures.includes('arm_enabled')
      && this.props.allActiveFeatures.includes('arm_enabled')) {
      this.props.getArmKillSwitchSettings();
    }

    if (prevProps.allActiveFeatures !== this.props.allActiveFeatures
      && !prevProps.allActiveFeatures.includes('auto_archive_on_label')
      && this.props.allActiveFeatures.includes('auto_archive_on_label')) {
      this.props.getAutoArchiveOnLabelSettings();
    }

    this.reconcileFeatureFlags(prevProps);

    const activeFeaturesChanged = prevProps.allActiveFeatures !== this.props.allActiveFeatures;
    if (activeFeaturesChanged || prevProps.modmedQueuedFaxSettings !== this.props.modmedQueuedFaxSettings) {
      const enabled = this.props.allActiveFeatures.includes(EMA_HIDE_NATIVE_SEND_FLAG.id);
      const visible = enabled || this.props.modmedQueuedFaxSettings?.has_ema_credentials === true
        || localStorage.m8_auto_upload_emr === 'EMA';
      this.setState(({ featureFlags }) => {
        const exists = featureFlags.some((flag) => flag.id === EMA_HIDE_NATIVE_SEND_FLAG.id);
        return {
          featureFlags: exists
            ? featureFlags.map((flag) => (activeFeaturesChanged && flag.id === EMA_HIDE_NATIVE_SEND_FLAG.id ? { ...flag, enabled } : flag))
            : [...featureFlags, ...(visible ? [{ ...EMA_HIDE_NATIVE_SEND_FLAG, enabled }] : [])],
        };
      });
    }
  }

  handleToggle = async (id, checked) => {
    if (id === 'manual_ai_on_uploads') {
      captureAnalytics('manual_ai_on_uploads_toggle_clicked', { enabled: checked });
    }

    // Config-backed features are edited in a modal instead of toggled directly.
    // Read the registry rather than a second hand-maintained list of the same ids.
    if (isConfigBackedFeature(id)) {
      // checked is the NEW value the switch is changing to
      this.openConfigModal(id, checked);
      return;
    }

    if (id === EMA_HIDE_NATIVE_SEND_FLAG.id) {
      captureAnalytics('ema_hide_native_send_toggled', { enabled: checked });
    }

    const { featureFlags } = this.state;
    const updatedFlags = featureFlags.map((flag) => {
      return flag.id === id ? { ...flag, enabled: checked } : flag;
    });

    this.setState({ featureFlags: updatedFlags });

    if (checked) {
      await this.props.enableFeature(id);
    } else {
      await this.props.disableFeature(id);
    }
  };

  handleModmedSettingsUpdate = async (settings) => {
    await this.props.updateModmedQueuedFaxSettings(settings);
    // Update the local state to reflect the new enabled status
    const { featureFlags } = this.state;
    const updatedFlags = featureFlags.map((flag) => {
      return flag.id === 'modmed_queued_fax' ? { ...flag, enabled: settings.enabled } : flag;
    });
    this.setState({ featureFlags: updatedFlags, showModmedSettingsModal: false, autoEnableModmed: false });
  };

  handleEmrAutoUploadCategorySettingsUpdate = async (settings) => {
    const result = await this.props.updateEmrAutoUploadCategorySettings(settings);
    if (result?.error) {
      throw result;
    }
    // The flag's enabled state syncs from the store in componentDidUpdate.
    this.setState({
      showUploadDedupSettingsModal: false,
      autoEnableUploadDedup: null,
    });
  };

  handleAutoArchiveSettingsUpdate = async (settings) => {
    const result = await this.props.updateAutoArchiveSettings(settings);
    if (result?.error) {
      throw result;
    }
    const { featureFlags } = this.state;
    const updatedFlags = featureFlags.map((flag) => {
      return flag.id === 'auto_archive_fax_after_autoupload' ? { ...flag, enabled: settings.enabled } : flag;
    });
    this.setState({
      featureFlags: updatedFlags,
      showAutoArchiveSettingsModal: false,
      autoEnableAutoArchive: false,
    });
  };

  handleAutoArchiveOnLabelUpdate = async (settings) => {
    const result = await this.props.updateAutoArchiveOnLabelSettings(settings);
    if (result?.error) {
      throw result;
    }
    this.setState({
      showAutoArchiveOnLabelModal: false,
      autoArchiveOnLabelRequestedEnabled: null,
    });
  };

  handleArmKillSwitchUpdate = async (settings) => {
    const result = await this.props.updateArmKillSwitchSettings(settings);
    if (result?.error) {
      throw result;
    }
    const { featureFlags } = this.state;
    const updatedFlags = featureFlags.map((flag) => {
      return flag.id === 'arm_enabled'
        ? { ...flag, enabled: !settingPaused(settings.auto_processing_paused) }
        : flag;
    });
    this.setState({ featureFlags: updatedFlags, showArmKillSwitchModal: false });
  };

  handleEmaResultsProviderSettingsUpdate = async (settings) => {
    const result = await this.props.updateEmaResultsProviderSettings(settings);
    if (result?.error) {
      throw result;
    }
    const { featureFlags } = this.state;
    const updatedFlags = featureFlags.map((flag) => {
      return flag.id === 'ema_results_ordering_provider' ? { ...flag, enabled: settings.enabled } : flag;
    });
    this.setState({
      featureFlags: updatedFlags,
      showEmaResultsProviderSettingsModal: false,
      emaResultsProviderRequestedEnabled: null,
    });
  };

  // Legacy admins can remove an existing grant, but cannot enable it. Use the
  // server-backed feature list so a failed removal keeps the off-switch visible.
  visibleFeatureFlags() {
    const { billingV2Routed, allActiveFeatures } = this.props;
    const manualAiEnabled = allActiveFeatures.includes('manual_ai_on_uploads');
    return this.state.featureFlags
      .filter((flag) => flag.id !== 'manual_ai_on_uploads' || billingV2Routed || manualAiEnabled)
      .map((flag) => {
        if (flag.id !== 'manual_ai_on_uploads') return flag;
        return {
          ...flag,
          enabled: manualAiEnabled,
          description: billingV2Routed ? flag.description
            : 'Manual AI is currently unavailable for your organization. You can turn this feature off. Contact Medsender before enabling it again.',
        };
      });
  }

  render() {
    const {
      showAutoArchiveSettingsModal,
      showAutoArchiveOnLabelModal,
      showModmedSettingsModal,
      showArmKillSwitchModal,
      showUploadDedupSettingsModal,
      showEmaResultsProviderSettingsModal,
    } = this.state;
    const {
      autoArchiveSettings,
      emaResultsProviderSettings,
      isLoading,
      modmedQueuedFaxSettings,
      armKillSwitchSettings,
      emrAutoUploadCategorySettings,
      emrTemplates,
      emrFacilities,
      emrTemplatesError,
      emrFacilitiesError,
    } = this.props;

    // Get fax numbers from localStorage
    const faxNumbers = JSON.parse(localStorage.getItem('associated_fax_numbers') || '[]');

    const columns = [
      {
        title: 'Features',
        dataIndex: 'name',
        key: 'name',
        render: (feature) => {
          return (
            <Tooltip key={feature}>
              {feature}
            </Tooltip>
          );
        },
      },
      {
        title: 'Feature Description',
        dataIndex: 'description',
        key: 'description',
        render: (description) => {
          return (
            <Tooltip key={description}>
              {description}
            </Tooltip>
          );
        },
      },
      {
        title: 'Enabled',
        dataIndex: 'enabled',
        key: 'enabled',
        render: (enabled, record) => {
          if (record.hasConfig) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={enabled}
                  onChange={() => this.openConfigModal(record.id, !enabled)}
                />
                <Button
                  type="link"
                  icon={<SettingOutlined />}
                  onClick={() => this.openConfigModal(record.id)}
                >
                  Configure
                </Button>
              </div>
            );
          }
          return (
            <Switch
              checked={enabled}
              onChange={(checked) => this.handleToggle(record.id, checked)}
            />
          );
        },
      },
    ];

    if (isLoading) return (<Spin size="large" />);

    return (
      // eslint-disable-next-line object-curly-newline
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '10px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex' }}>
            <h2>Toggle Features</h2>
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <Table
            size="small"
            dataSource={this.visibleFeatureFlags()}
            columns={columns}
            rowKey={(feature) => feature.id}
            bordered
            pagination={false}
          />
        </div>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="Auto-Archive Settings"
          visible={showAutoArchiveSettingsModal}
          onCancel={() => this.setState({ showAutoArchiveSettingsModal: false, autoEnableAutoArchive: false })}
          footer={null}
          width={720}
          destroyOnClose
        >
          <AutoArchiveSettings
            settings={autoArchiveSettings}
            updateSettings={this.handleAutoArchiveSettingsUpdate}
            autoEnable={this.state.autoEnableAutoArchive}
          />
        </Modal>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="Archive by Label or Category"
          visible={showAutoArchiveOnLabelModal}
          onCancel={() => this.setState({
            showAutoArchiveOnLabelModal: false,
            autoArchiveOnLabelRequestedEnabled: null,
          })}
          footer={null}
          width={720}
          destroyOnClose
        >
          <AutoArchiveOnLabelSettings
            settings={this.props.autoArchiveOnLabelSettings}
            updateSettings={this.handleAutoArchiveOnLabelUpdate}
            requestedEnabled={this.state.autoArchiveOnLabelRequestedEnabled}
            labels={this.props.labels}
            documentCategories={this.props.documentCategories}
          />
        </Modal>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="Duplicate Upload Enforcement"
          visible={showUploadDedupSettingsModal}
          onCancel={() => this.setState({
            showUploadDedupSettingsModal: false,
            autoEnableUploadDedup: null,
          })}
          footer={null}
          width={720}
          destroyOnClose
        >
          <EmrAutoUploadCategorySettings
            settings={emrAutoUploadCategorySettings || {}}
            updateSettings={this.handleEmrAutoUploadCategorySettingsUpdate}
            canConfigureCategories={false}
            canConfigureUploadDedup
            autoEnableUploadDedup={this.state.autoEnableUploadDedup}
          />
        </Modal>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="Results Ordering Provider Settings"
          visible={showEmaResultsProviderSettingsModal}
          onCancel={() => this.setState({
            showEmaResultsProviderSettingsModal: false,
            emaResultsProviderRequestedEnabled: null,
          })}
          footer={null}
          width={720}
          destroyOnClose
        >
          <EmaResultsProviderSettings
            settings={emaResultsProviderSettings?.settings}
            providerCount={emaResultsProviderSettings?.provider_count}
            updateSettings={this.handleEmaResultsProviderSettingsUpdate}
            requestedEnabled={this.state.emaResultsProviderRequestedEnabled}
          />
        </Modal>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="ModMed Queued Fax Settings"
          visible={showModmedSettingsModal}
          onCancel={() => this.setState({ showModmedSettingsModal: false, autoEnableModmed: false })}
          footer={null}
          width={1100}
          destroyOnClose
        >
          <ModmedQueuedFaxSettings
            settings={modmedQueuedFaxSettings}
            faxNumbers={faxNumbers}
            templates={emrTemplates}
            facilities={emrFacilities}
            templatesLoading={!this.props.emrTemplatesLoaded && !emrTemplatesError}
            facilitiesLoading={!this.props.emrFacilitiesLoaded && !emrFacilitiesError}
            templatesError={emrTemplatesError}
            facilitiesError={emrFacilitiesError}
            updateSettings={this.handleModmedSettingsUpdate}
            autoEnable={this.state.autoEnableModmed}
          />
        </Modal>

        <Modal
          wrapClassName="settings-modal-wrap"
          title="ARM Auto-Processing"
          visible={showArmKillSwitchModal}
          onCancel={() => this.setState({ showArmKillSwitchModal: false })}
          footer={null}
          width={620}
          destroyOnClose
        >
          <KillSwitchSettings
            settings={armKillSwitchSettings}
            updateSettings={this.handleArmKillSwitchUpdate}
          />
        </Modal>
      </div>
    );
  }
}

ToggleFeatures.defaultProps = {
  autoArchiveOnLabelSettings: null,
  labels: [],
  documentCategories: [],
  autoArchiveSettings: {},
  emaResultsProviderSettings: {},
  isLoading: false,
  modmedQueuedFaxSettings: {},
  armKillSwitchSettings: {},
  emrAutoUploadCategorySettings: {},
  emrTemplates: [],
  emrFacilities: [],
  emrTemplatesLoaded: false,
  emrFacilitiesLoaded: false,
  emrTemplatesError: null,
  emrFacilitiesError: null,
  billingV2Routed: false,
};

ToggleFeatures.propTypes = {
  isLoading: PropTypes.bool,
  allActiveFeatures: PropTypes.array.isRequired,
  billingV2Routed: PropTypes.bool,
  enableFeature: PropTypes.func.isRequired,
  disableFeature: PropTypes.func.isRequired,
  autoArchiveSettings: PropTypes.object,
  emaResultsProviderSettings: PropTypes.object,
  modmedQueuedFaxSettings: PropTypes.object,
  armKillSwitchSettings: PropTypes.object,
  emrAutoUploadCategorySettings: PropTypes.object,
  emrTemplates: PropTypes.array,
  emrFacilities: PropTypes.array,
  emrTemplatesLoaded: PropTypes.bool,
  emrFacilitiesLoaded: PropTypes.bool,
  emrTemplatesError: PropTypes.string,
  emrFacilitiesError: PropTypes.string,
  getAutoArchiveSettings: PropTypes.func.isRequired,
  updateAutoArchiveSettings: PropTypes.func.isRequired,
  getAutoArchiveOnLabelSettings: PropTypes.func.isRequired,
  updateAutoArchiveOnLabelSettings: PropTypes.func.isRequired,
  getAllLabelsForAutoArchive: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  autoArchiveOnLabelSettings: PropTypes.object,
  labels: PropTypes.array,
  documentCategories: PropTypes.array,
  getEmrAutoUploadCategorySettings: PropTypes.func.isRequired,
  updateEmrAutoUploadCategorySettings: PropTypes.func.isRequired,
  getEmaResultsProviderSettings: PropTypes.func.isRequired,
  updateEmaResultsProviderSettings: PropTypes.func.isRequired,
  getModmedQueuedFaxSettings: PropTypes.func.isRequired,
  updateModmedQueuedFaxSettings: PropTypes.func.isRequired,
  getArmKillSwitchSettings: PropTypes.func.isRequired,
  updateArmKillSwitchSettings: PropTypes.func.isRequired,
  getEmrTemplates: PropTypes.func.isRequired,
  getEmrFacilities: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    isLoading: state.userProfile.isLoading,
    allActiveFeatures: state.userProfile.allActiveFeatures,
    billingV2Routed: state.userProfile.billingV2Routed,
    autoArchiveSettings: state.userProfile.autoArchiveSettings,
    autoArchiveOnLabelSettings: state.userProfile.autoArchiveOnLabelSettings,
    labels: state.userProfile.autoArchiveLabels,
    documentCategories: state.userProfile.allDocumentCategories.categories,
    emaResultsProviderSettings: state.userProfile.emaResultsProviderSettings,
    modmedQueuedFaxSettings: state.userProfile.modmedQueuedFaxSettings,
    armKillSwitchSettings: state.userProfile.armKillSwitchSettings,
    emrAutoUploadCategorySettings: state.userProfile.emrAutoUploadCategorySettings,
    emrTemplates: state.userProfile.emrTemplates,
    emrFacilities: state.userProfile.emrFacilities,
    emrTemplatesLoaded: state.userProfile.emrTemplatesLoaded,
    emrFacilitiesLoaded: state.userProfile.emrFacilitiesLoaded,
    emrTemplatesError: state.userProfile.emrTemplatesError,
    emrFacilitiesError: state.userProfile.emrFacilitiesError,
  }),
  {
    getAutoArchiveSettings,
    updateAutoArchiveSettings,
    getAutoArchiveOnLabelSettings,
    updateAutoArchiveOnLabelSettings,
    getAllLabelsForAutoArchive,
    getDocumentCategories,
    getEmrAutoUploadCategorySettings,
    updateEmrAutoUploadCategorySettings,
    getEmaResultsProviderSettings,
    updateEmaResultsProviderSettings,
    getModmedQueuedFaxSettings,
    updateModmedQueuedFaxSettings,
    getArmKillSwitchSettings,
    updateArmKillSwitchSettings,
    getEmrTemplates,
    getEmrFacilities,
  },
)(ToggleFeatures);
